/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the drug shipment status modal component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { Button, DialogTitle, DialogContent, Dialog, DialogActions } from '@material-ui/core';

import * as Yup from 'yup';

import { FormRenderProps } from 'react-final-form';
import { useSnackbar } from 'notistack';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import * as Dtos from '../../../api/Neon.Dtos';
import { AsyncForm } from '../../../components/FinalForm/AsyncForm';
import { setFieldValue } from '../../../components/FinalForm/mutators/setFieldValue';
import { DrugShipmentActions } from '../../../store/modules/drugmanagement/drugshipment';

import { getShipmentStatusText } from '../../../utilities/getShipmentStatusText';
import HiddenField from '../../../components/FinalForm/FinalFormControls/HiddenField';
import useAsyncFunction from '../../../hooks/useAsyncFunction';

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface IDrugShipmentStatusModalProps {
    drugShipment: Dtos.DrugShipment | null;
    modalOpen: boolean;
    setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    newStatus?: Dtos.ShipmentStatus | null;
    onCloseClick?: () => void;
    onFormSave?: () => void;
}

interface IDrugShipmentStatusFormProps extends FormRenderProps<Dtos.DrugShipment> {
    drugShipment: Dtos.DrugShipment | null;
    setModalOpen: (open: boolean) => void;
    newStatus: Dtos.ShipmentStatus;
    onFormSave?: () => void;
}

interface IDrugShipmentStatusForm extends Partial<Dtos.DrugShipment> {
}

const formSubscriptionParams = {
    submitting: true,
    pristine: false,
    submitError: true,
    dirtySinceLastSubmit: true,
    hasValidationErrors: true,
    valid: true,
    validating: true,
    errors: true,
    form: true
};

const validation = Yup.object().shape<IDrugShipmentStatusForm>({
});

const DrugShipmentStatusForm: React.FunctionComponent<IDrugShipmentStatusFormProps> = ({
    newStatus,
    setModalOpen,
    onFormSave,
    ...formRenderProps
}) => {
    const { enqueueSnackbar } = useSnackbar();

    const onOkClick = React.useCallback(async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        try {
            const response = await formRenderProps.handleSubmit();

            if (response) {
                setModalOpen(false);

                if (onFormSave) {
                    onFormSave();
                }
            }
        }
        catch (error: any) {
            const errorMessage = error.message ?
                error.message :
                'Unknown error';

            enqueueSnackbar(`An error occurred while attempting to change the drug shipment's status: ${errorMessage}`, { variant: 'error' });
        }
    }, [setModalOpen, enqueueSnackbar, formRenderProps, onFormSave]);

    const onCloseClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setModalOpen(false);
    }, [setModalOpen]);

    const formDisabled = React.useMemo(() => {
        return formRenderProps.submitting;
    }, [formRenderProps.submitting]);

    const dialogText = React.useMemo(() => {
        const statusDesc = getShipmentStatusText(newStatus);

        switch (newStatus) {
            case Dtos.ShipmentStatus.Ordered:
                return `This will set the drug shipment's status to ${statusDesc} and send the drug shipment form to the depot. Do you wish to continue?`;
            case Dtos.ShipmentStatus.InTransit:
                return `This will set the drug shipment's status to ${statusDesc}. Do you wish to continue?`;
            case Dtos.ShipmentStatus.Available:
                return `This will set the drug shipment's status to ${statusDesc} and add the drugs to the institution's stock. Do you wish to continue?`;
            case Dtos.ShipmentStatus.Lost:
                return `This will set the drug shipment's status to ${statusDesc}. Do you wish to continue?`;
            case Dtos.ShipmentStatus.QuarantinedFromTransit:
                return `This will set the drug shipment's status to ${statusDesc}. The drugs will not be added to the pharmacy's stock. Do you wish to continue?`;
            default:
                return undefined;
        }
    }, [newStatus]);

    return (
        <>
            <DialogContent>
                {dialogText}
                <HiddenField
                    fieldName="shipmentStatus"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onCloseClick} color="primary" disabled={formDisabled}>
                    Cancel
                </Button>
                <Button onClick={onOkClick}
                    color="primary" autoFocus disabled={formDisabled}>
                    OK
                </Button>
            </DialogActions>
        </>
    );
};

const DrugShipmentStatusModal: React.FunctionComponent<IDrugShipmentStatusModalProps> = (props) => {
    if (!props.newStatus || !props.drugShipment) {
        return null;
    }

    return (
        <InnerDrugShipmentStatusModal {...props} />
    );
}

const InnerDrugShipmentStatusModal: React.FunctionComponent<IDrugShipmentStatusModalProps> = ({
    drugShipment,
    newStatus,
    modalOpen,
    setModalOpen,
    onCloseClick,
    onFormSave
}) => {
    const initialValues = Object.assign({}, drugShipment, { shipmentStatus: newStatus });

    const drugShipmentForm = React.useCallback((props: FormRenderProps<Dtos.DrugShipment>) => {
        return (
            <DrugShipmentStatusForm
                drugShipment={initialValues}
                newStatus={newStatus!}
                setModalOpen={setModalOpen}
                onFormSave={onFormSave}
                {...props}
            />
        );
    }, [setModalOpen, initialValues, newStatus, onFormSave]);

    const asyncSave = useAsyncFunction(DrugShipmentActions.setStatus, DrugShipmentActions.setStatusSuccess, DrugShipmentActions.setStatusFailure);

    return (
        <>
            <Dialog
                open={modalOpen}
                onClose={onCloseClick}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    Change Drug Shipment Status
                </DialogTitle>
                <AsyncForm
                    initialValues={initialValues}
                    validate={validation}
                    onSubmit={asyncSave}
                    formSubscription={formSubscriptionParams}
                    validationDebounceTimeout={200}
                    component={drugShipmentForm}
                    mutators={{
                        setFieldValue
                    }}
                />
            </Dialog>
        </>
    );
}

export default DrugShipmentStatusModal;